import type { AirdropFarmingBotDto } from "@cede/types";
import Decimal from "decimal.js";

/**
 * Total cost = fees - realizedPnl (net of trading gains/losses).
 * Positive PnL reduces cost, negative PnL increases it.
 * Uses realized PnL only (not unrealized) so cost is stable and doesn't
 * fluctuate with mark-to-market on the open position.
 * Can be negative when trading profits exceed fees.
 */
function computeTotalCost(bot: AirdropFarmingBotDto): Decimal {
  const fees = new Decimal(bot.totalFeesUsdc);
  const realizedPnl = new Decimal(bot.realizedPnlUsdc);
  return fees.minus(realizedPnl);
}

/**
 * Cost per $1,000 of volume generated.
 */
export function computeCostPer1kVolume(bot: AirdropFarmingBotDto): Decimal | null {
  const volume = new Decimal(bot.totalVolumeUsdc);
  if (volume.isZero()) return null;
  return computeTotalCost(bot).div(volume).mul(1000);
}

/**
 * Safety buffer = maxLoss + realizedPnl + unrealizedPnl.
 * Mirrors the actual stop condition in shouldStopMaxLoss:
 *   totalPnl < -maxLoss → stop
 * So buffer = maxLoss + totalPnl = distance to auto-stop.
 * When this reaches 0, the bot auto-stops.
 */
export function computeSafetyBuffer(bot: AirdropFarmingBotDto): Decimal {
  const maxLoss = new Decimal(bot.maxLossUsdc);
  const realizedPnl = new Decimal(bot.realizedPnlUsdc);
  const unrealizedPnl = new Decimal(bot.unrealizedPnlUsdc);
  return maxLoss.plus(realizedPnl).plus(unrealizedPnl).minus(bot.totalFeesUsdc);
}

/**
 * Estimated time remaining based on current fill rate and elapsed time.
 * Returns milliseconds, or null if not enough data.
 */
export function computeEtaMs(bot: AirdropFarmingBotDto): number | null {
  const fillRate = bot.fillRatePct;
  if (fillRate <= 0) return null;

  const elapsed = Date.now() - new Date(bot.createdAt).getTime();
  const remaining = (100 / fillRate - 1) * elapsed;
  return Math.max(0, remaining);
}

/**
 * Size per grid level: capital * leverage / (numLevels * 2)
 * The *2 is because we have both buy and sell sides.
 */
export function computeSizePerLevel(capital: string, leverage: number, numLevels: number): Decimal {
  return new Decimal(capital).mul(leverage).div(numLevels * 2);
}

/**
 * Estimated total fees to reach target volume.
 * Each dollar of volume costs makerFeeRate in fees.
 */
export function computeEstimatedFeesToTarget(targetVolume: string, makerFeeRate: string): Decimal {
  return new Decimal(targetVolume).mul(makerFeeRate);
}

/**
 * Estimated fee range to reach target volume.
 * Returns [bestCase (all maker), worstCase (all taker)].
 */
export function computeEstimatedFeeRange(
  targetVolume: string,
  feeRates: { maker: number; taker: number },
): { bestCase: Decimal; worstCase: Decimal } {
  const vol = new Decimal(targetVolume);
  return {
    bestCase: vol.mul(feeRates.maker),
    worstCase: vol.mul(feeRates.taker),
  };
}

/**
 * Number of full grid cycles needed to reach target volume.
 * One cycle = all buy + sell orders fill = capital × leverage in volume.
 */
export function computeGridCycles(capital: string, leverage: number, targetVolume: string): Decimal {
  const notional = new Decimal(capital).mul(leverage);
  if (notional.isZero()) return new Decimal(0);
  return new Decimal(targetVolume).div(notional);
}

/**
 * Safety budget = maxLoss - estimated fees.
 * This is the budget left for stop-loss losses after covering fees.
 */
export function computeSafetyBudget(maxLoss: string, targetVolume: string, makerFeeRate: string): Decimal {
  return new Decimal(maxLoss).minus(computeEstimatedFeesToTarget(targetVolume, makerFeeRate));
}

/**
 * Suggested max loss based on target volume.
 * Uses a conservative $1 per $1k of volume (covers fees + adverse selection).
 * Returns ceiling-rounded integer so the suggestion is a clean number.
 */
export function computeSuggestedMaxLoss(targetVolume: string, costPer1k: number): number {
  const vol = new Decimal(targetVolume);
  if (vol.lte(0)) return 0;
  return Math.ceil(vol.div(1000).mul(costPer1k).toNumber());
}

/**
 * Estimated time to reach target volume in hours.
 * Based on grid cycles needed and estimated cycles per hour for the risk profile.
 */
export function computeEstimatedTimeHours(
  capital: string,
  leverage: number,
  targetVolume: string,
  cyclesPerHour: number,
): number {
  const gridCycles = computeGridCycles(capital, leverage, targetVolume);
  return gridCycles.toNumber() / cyclesPerHour;
}
